import React, { FC, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../../../app/modules/auth';
import { KTSVG } from '../../../_metronic/helpers';

const API_URL = process.env.REACT_APP_API_URL;

interface AddcustomerProps {
  show: boolean;
  handleClose: () => void;
}

const Addcustomer: FC<AddcustomerProps> = ({ show, handleClose }) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    customer_firstname: '',
    customer_secondname: '',
    customer_phone_number: '+254',
    customer_email: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isEmailUserGenerated, setIsEmailUserGenerated] = useState(false);

  // Function to generate a unique email from names
  const generateEmailFromNames = (firstName: string, secondName: string): string => {
    if (!firstName || !secondName) return '';
    
    const cleanFirstName = firstName.toLowerCase().replace(/[^a-z]/g, '');
    const cleanSecondName = secondName.toLowerCase().replace(/[^a-z]/g, '');
    
    if (!cleanFirstName || !cleanSecondName) return '';
    
    // Generate a random number for uniqueness
    const randomNum = Math.floor(Math.random() * 9999) + 1;
    
    // Different email formats for variety
    const formats = [
      `${cleanFirstName}.${cleanSecondName}${randomNum}@gmail.com`,
      `${cleanFirstName}${cleanSecondName}${randomNum}@gmail.com`,
      `${cleanFirstName.charAt(0)}${cleanSecondName}${randomNum}@gmail.com`,
      `${cleanFirstName}${cleanSecondName.charAt(0)}${randomNum}@gmail.com`,
    ];
    
    // Randomly select one of the formats
    const selectedFormat = formats[Math.floor(Math.random() * formats.length)];
    return selectedFormat;
  };

  // Auto-generate email when names change (if user hasn't manually entered email)
  useEffect(() => {
    if (!isEmailUserGenerated && formData.customer_firstname && formData.customer_secondname) {
      const generatedEmail = generateEmailFromNames(formData.customer_firstname, formData.customer_secondname);
      setFormData(prev => ({ ...prev, customer_email: generatedEmail }));
    }
  }, [formData.customer_firstname, formData.customer_secondname, isEmailUserGenerated]);

  const addCustomerMutation = useMutation(
    async (newCustomer: any) => {
      const response = await axios.post(`${API_URL}/customers`, {
        ...newCustomer,
        user_id: currentUser?.user_id,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('allcustomers');
        queryClient.invalidateQueries('customersSummary');
        queryClient.invalidateQueries('topCustomers');
        setFormData({
          customer_firstname: '',
          customer_secondname: '',
          customer_phone_number: '+254',
          customer_email: '',
        });
        setError(null);
        setIsEmailUserGenerated(false);
        handleClose();
      },
      onError: (error: any) => {
        setError(error.response?.data?.message || 'Failed to add customer');
      },
    }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Convert first name and second name to uppercase
    const processedValue = (name === 'customer_firstname' || name === 'customer_secondname') 
      ? value.toUpperCase() 
      : value;
    
    // Track if user manually changes email
    if (name === 'customer_email') {
      setIsEmailUserGenerated(true);
    }
    
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.customer_firstname || !formData.customer_secondname || !formData.customer_phone_number || !formData.customer_email) {
      setError('First name, second name, phone number, and email are required');
      return;
    }
    addCustomerMutation.mutate(formData);
  };

  // Handle modal close - reset email generation state
  const handleModalClose = () => {
    setIsEmailUserGenerated(false);
    handleClose();
  };

  return (
    <Modal
      className='modal fade'
      id='kt_modal_add_customer'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      centered
      dialogClassName='modal-lg'
      aria-hidden='true'
      onHide={handleModalClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='fw-bolder'>Add New Customer</h3>
          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            onClick={handleModalClose}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          <form className='form' noValidate onSubmit={handleSubmit}>
            {/* begin::Input group */}
            <div className='row'>
              <div className='col-lg-6 fv-row mb-7'>
                {/* begin::Label */}
                <label className='required fw-bold fs-6 mb-2'>First Name</label>
                {/* end::Label */}
                <input
                  type='text'
                  name='customer_firstname'
                  placeholder='Enter first name'
                  className='form-control form-control-lg form-control-solid'
                  autoComplete='off'
                  value={formData.customer_firstname}
                  onChange={handleChange}
                />
              </div>

              <div className='col-lg-6 fv-row mb-7'>
                {/* begin::Label */}
                <label className='required fw-bold fs-6 mb-2'>Second Name</label>
                {/* end::Label */}
                <input
                  type='text'
                  name='customer_secondname'
                  placeholder='Enter second name'
                  className='form-control form-control-lg form-control-solid'
                  autoComplete='off'
                  value={formData.customer_secondname}
                  onChange={handleChange}
                />
              </div>
            </div>
            {/* end::Input group */}

            {/* begin::Input group */}
            <div className='row'>
              <div className='col-lg-6 fv-row mb-7'>
                {/* begin::Label */}
                <label className='required fw-bold fs-6 mb-2'>Phone Number</label>
                {/* end::Label */}
                <input
                  type='text'
                  name='customer_phone_number'
                  placeholder='e.g. +254712345678'
                  className='form-control form-control-lg form-control-solid'
                  autoComplete='off'
                  value={formData.customer_phone_number}
                  onChange={handleChange}
                />
              </div>

              <div className='col-lg-6 fv-row mb-7'>
                {/* begin::Label */}
                <label className='required fw-bold fs-6 mb-2'>Email</label>
                {/* end::Label */}
                <input
                  type='email'
                  name='customer_email'
                  placeholder='Email will be auto-generated or enter custom email'
                  className='form-control form-control-lg form-control-solid'
                  autoComplete='off'
                  value={formData.customer_email}
                  onChange={handleChange}
                />
                {!isEmailUserGenerated && formData.customer_email && (
                  <div className='form-text text-muted mt-1'>
                    <small>Auto-generated email. You can modify it if needed.</small>
                  </div>
                )}
              </div>
            </div>
            {/* end::Input group */}

            {/* Error display */}
            {error && (
              <div className='fv-row mb-7'>
                <div className='alert alert-danger d-flex align-items-center p-5'>
                  <KTSVG
                    path='/media/icons/duotune/general/gen044.svg'
                    className='svg-icon-2hx svg-icon-danger me-4'
                  />
                  <div className='d-flex flex-column'>
                    <span className='fw-bold fs-6'>{error}</span>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        <div className='modal-footer border-0 mt-n5'>
          <button type='button' className='btn btn-light-primary' onClick={handleModalClose}>
            Cancel
          </button>
          <button
            id='submit'
            type='button'
            className='btn btn-primary'
            onClick={handleSubmit}
            disabled={addCustomerMutation.isLoading || !formData.customer_firstname || !formData.customer_secondname || !formData.customer_phone_number || !formData.customer_email}
          >
            {!addCustomerMutation.isLoading && <span className='indicator-label'>Add Customer</span>}
            {addCustomerMutation.isLoading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export { Addcustomer };